import {Button, Dialog} from '@primer/react'
import {InlineMessage} from '@primer/react/experimental'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useRef, useState} from 'react'
import {useUnstackPullRequestsMutation} from '../mutations/use-unstack-pull-requests-mutation'
import {useQueryClient} from '@github-ui/react-query'
import {useHeaderPageDataQueryKey} from '../page-data/loaders/use-header-page-data'

type UnstackButtonProps = {
  stackId: number
  setIsEditing?: (isEditing: boolean) => void
}

export function UnstackButton({setIsEditing, stackId}: UnstackButtonProps) {
  const isPullRequestStacksEnabled = useFeatureFlag('pull_request_stacks')

  const [isConfirmingUnstack, setIsConfirmingUnstack] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState('')

  const queryClient = useQueryClient()
  const headerPageDataQueryKey = useHeaderPageDataQueryKey()
  const {mutate: mutateUnstackPullRequests, isPending: isLoading} = useUnstackPullRequestsMutation()

  const unstackButtonRef = useRef<HTMLButtonElement>(null)

  const onRequestUnstack = () => {
    setIsConfirmingUnstack(true)
  }

  const onCancelUnstack = () => {
    setIsConfirmingUnstack(false)
    setErrorMessage('')
  }

  const handleUnstackError = (message: string) => {
    setErrorMessage(message)
  }

  const onConfirmUnstack = () => {
    setErrorMessage('')
    mutateUnstackPullRequests(
      {stackId},
      {
        onError: error => handleUnstackError(error.message),
        onSuccess: async () => {
          try {
            // We're using refetchQueries here instead of invalidateQueries in order to ensure that the
            // page data is updated *before* the confirmation dialog is closed, for a smoother overall UX.
            await queryClient.refetchQueries({queryKey: headerPageDataQueryKey}, {throwOnError: true})
            setIsEditing?.(false)
            setIsConfirmingUnstack(false)
          } catch {
            handleUnstackError('Something unexpected happened. Please refresh the page.')
          }
        },
      },
    )
  }

  if (!isPullRequestStacksEnabled) {
    return null
  }

  if (!stackId) {
    return null
  }

  return (
    <>
      <Button ref={unstackButtonRef} onClick={onRequestUnstack} size={'small'}>
        Unstack
      </Button>
      {isConfirmingUnstack && (
        <Dialog
          title="Are you sure you want to unstack these pull requests?"
          returnFocusRef={unstackButtonRef}
          onClose={onCancelUnstack}
          footerButtons={[
            {buttonType: 'default', content: 'Cancel', onClick: onCancelUnstack},
            {
              buttonType: 'primary',
              content: 'Confirm',
              onClick: onConfirmUnstack,
              // aria-disabled must be not set when false to avoid broken styling
              'aria-disabled': isLoading ? 'true' : undefined,
              disabled: isLoading,
              loading: isLoading,
            },
          ]}
        >
          All pull requests will be removed from the stack. The pull requests will otherwise be unchanged, e.g. their
          base branch will not be retargeted, commit history will remain the same, etc.
          {errorMessage && (
            <InlineMessage className="mt-2" variant="critical">
              {errorMessage}
            </InlineMessage>
          )}
          {/* Loading message accessible to screen readers only. See https://primer.style/components/button#button-loading-state */}
          <span className="sr-only" aria-live="polite">
            {isLoading ? 'Unstacking pull requests in progress.' : ''}
          </span>
        </Dialog>
      )}
    </>
  )
}
